/** Room config a support person would otherwise have to join the room to see. */

export interface FlagRow {
	name: string;
	enabled: boolean;
	/** Differs from the platform default, i.e. set specifically for this room. */
	overridden: boolean;
	/** What the default would have been. Undefined when the flag is room-only. */
	defaultValue?: boolean;
}

/**
 * The override route returns the complete effective set, not a sparse diff, so
 * an override is only visible by comparison. Flags in one set and not the other
 * are kept - a room-only flag is worth seeing.
 */
export function diffFeatureFlags(
	effective?: Record<string, boolean> | null,
	defaults?: Record<string, boolean> | null,
): FlagRow[] {
	if (!effective) return [];

	const defaultFlags = defaults ?? {};
	const names = Array.from(new Set([...Object.keys(effective), ...Object.keys(defaultFlags)]));

	return names
		.map((name) => {
			const enabled = !!effective[name];
			const hasDefault = name in defaultFlags;
			return {
				name,
				enabled,
				defaultValue: hasDefault ? !!defaultFlags[name] : undefined,
				overridden: !hasDefault || !!defaultFlags[name] !== enabled,
			};
		})
		// Overridden first: the flags that explain odd behaviour sort to the top.
		.sort((a, b) => (
			Number(b.overridden) - Number(a.overridden)
			|| Number(b.enabled) - Number(a.enabled)
			|| a.name.localeCompare(b.name)
		));
}

/** `settings_json` is the whole reducer, mostly accordion state; these matter. */
export const REPORTED_SETTINGS: { key: string; label: string }[] = [
	{ key: 'askToJoin', label: 'Ask to join' },
	{ key: 'entryWithoutProducer', label: 'Entry without producer' },
	{ key: 'useModeratedQuestions', label: 'Moderated questions' },
	{ key: 'backstageCommsActive', label: 'Backstage comms' },
	{ key: 'selectedResolution', label: 'Broadcast resolution' },
	{ key: 'defaultResolution', label: 'Default resolution' },
	{ key: 'useDefaultEndpoint', label: 'Default endpoint' },
	{ key: 'useEmailInvites', label: 'Email invites' },
	{ key: 'useShowboard', label: 'Showboard' },
	{ key: 'presenterAudienceTab', label: 'Presenter audience tab' },
	{ key: 'feedScaling', label: 'Feed scaling' },
	{ key: 'clearShot', label: 'Clear shot' },
];

export interface SettingRow {
	label: string;
	value: string;
}

const formatValue = (value: unknown): string => {
	if (typeof value === 'boolean') return value ? 'on' : 'off';
	return String(value);
};

/** Formats the curated settings, skipping any the room has never saved. */
export function reportedSettings(settings?: Record<string, any>): SettingRow[] {
	if (!settings) return [];

	return REPORTED_SETTINGS
		.filter(({ key }) => settings[key] !== undefined)
		.map(({ key, label }) => ({ label, value: formatValue(settings[key]) }));
}
